/**
 * AI Chat Statistics Service
 * 
 * Service để gọi API thống kê lịch sử chat CVHT AI
 */

import axios from 'axios';

const API_BASE = '/api/v1/ai-chat-statistics';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
});

// Types
export interface OverviewStatistics {
  period: {
    days: number;
    startDate: string | null;
    endDate: string | null;
  };
  overview: {
    totalQueries: number;
    uniqueUsers: number;
    totalSessions: number;
    avgResponseTime: number;
    totalTokensUsed: number;
    groundedResponses: number;
    groundingRate: string | number;
  };
  today: {
    queries: number;
    users: number;
    avgResponseTime: number;
  };
  growth: {
    queries: number;
    users: number;
  };
}

export interface QueryTrend {
  date: string;
  queryCount: number;
  userCount: number;
  avgResponseTime: number;
  tokensUsed: number;
  groundedCount: number;
  groundingRate: number;
}

export interface TopQuery {
  rank: number;
  query: string;
  count: number;
  uniqueUsers: number;
  avgResponseTime: number;
  lastAsked: string;
}

export interface ChatLog {
  logId: number;
  userId: number;
  sessionId: number;
  documentId: number | null;
  query: string;
  response: string;
  responseTime: number;
  tokensUsed: number;
  hasGrounding: boolean;
  sourceDocuments: any[];
  createdAt: string;
  user: {
    username: string;
    fullName: string;
    studentId: string | null;
  };
}

export interface UserStatistic {
  rank: number;
  userId: number;
  username: string;
  fullName: string;
  studentId: string | null;
  className: string | null;
  queryCount: number;
  sessionCount: number;
  avgResponseTime: number;
  totalTokensUsed: number;
  firstQuery: string;
  lastQuery: string;
}

export interface ResponseTimeDistribution {
  timeRange: string;
  count: number;
  avgTime: number;
}

export interface HeatmapData {
  day: string;
  hour: number;
  value: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ChatLogsFilters {
  page?: number;
  limit?: number;
  search?: string;
  userId?: number;
  sessionId?: number;
  startDate?: string;
  endDate?: string;
  hasGrounding?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// API Functions
export const getOverviewStatistics = async (days: number = 30): Promise<OverviewStatistics> => {
  const response = await axios.get(`${API_BASE}/overview`, {
    params: { days },
    headers: getAuthHeaders()
  });
  return response.data.data;
};

export const getQueryTrends = async (days: number = 30, groupBy: 'day' | 'week' | 'month' | 'hour' = 'day'): Promise<QueryTrend[]> => {
  const response = await axios.get(`${API_BASE}/trends`, {
    params: { days, groupBy },
    headers: getAuthHeaders()
  });
  return response.data.data;
};

export const getTopQueries = async (days: number = 30, limit: number = 20): Promise<TopQuery[]> => {
  const response = await axios.get(`${API_BASE}/top-queries`, {
    params: { days, limit },
    headers: getAuthHeaders()
  });
  return response.data.data;
};

export const getChatLogs = async (filters: ChatLogsFilters): Promise<{ data: ChatLog[]; pagination: Pagination }> => {
  const response = await axios.get(`${API_BASE}/logs`, {
    params: filters,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getUserStatistics = async (days: number = 30, limit: number = 20): Promise<UserStatistic[]> => {
  const response = await axios.get(`${API_BASE}/users`, {
    params: { days, limit },
    headers: getAuthHeaders()
  });
  return response.data.data;
};

export const getResponseTimeDistribution = async (days: number = 30): Promise<ResponseTimeDistribution[]> => {
  const response = await axios.get(`${API_BASE}/response-time`, {
    params: { days },
    headers: getAuthHeaders()
  });
  return response.data.data;
};

export const getActivityHeatmap = async (days: number = 30): Promise<HeatmapData[]> => {
  const response = await axios.get(`${API_BASE}/heatmap`, {
    params: { days },
    headers: getAuthHeaders()
  });
  return response.data.data;
};

export const exportChatLogs = async (params: { startDate?: string; endDate?: string; userId?: number; limit?: number }): Promise<void> => {
  const response = await axios.get(`${API_BASE}/export`, {
    params,
    headers: getAuthHeaders(),
    responseType: 'blob'
  });

  // Download file
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `ai_chat_logs_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default {
  getOverviewStatistics,
  getQueryTrends,
  getTopQueries,
  getChatLogs,
  getUserStatistics,
  getResponseTimeDistribution,
  getActivityHeatmap,
  exportChatLogs
};

